import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { isPromoActive, PROMO_END_DISPLAY } from '../components/PromoBanner';
import { useAuth } from '../context/AuthContext';
import { paymentAPI, authAPI } from '../services/api';
import UpgradeModal from '../components/UpgradeModal';
import DeleteAccountModal from '../components/DeleteAccountModal';

export default function MyAccountPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [cancelling,   setCancelling]   = useState(false);
  const [showUpgrade,  setShowUpgrade]  = useState(false);
  const [showDelete,   setShowDelete]   = useState(false);
  const [error,        setError]        = useState('');
  const [notice,       setNotice]       = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await paymentAPI.getSubscription();
      setSubscription(data.subscription || null);
      if (data.plan && data.plan !== user?.plan) {
        setUser(prev => ({ ...prev, plan: data.plan }));
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load subscription details. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (iso) => {
    if (!iso) return 'the end of the current period';
    try {
      return new Date(iso).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    } catch { return iso; }
  };

  const cycleLabel = (planKey) => {
    if (planKey === 'PRO_ANNUAL')  return 'PRO Annual — ₹1,799 / year';
    if (planKey === 'PRO_MONTHLY') return 'PRO Monthly — ₹199 / month';
    return planKey || 'PRO';
  };

  // Build a pre-filled "Request refund" mailto so the user doesn't have to
  // figure out what info to send. They edit the payment ID + date inline.
  const refundMailtoHref = () => {
    const planLabel = subscription?.planKey ? cycleLabel(subscription.planKey) : 'PRO';
    const subject   = `Refund request — CreatorKhata ${planLabel}`;
    const body =
`Hi,

I would like to request a refund for my CreatorKhata subscription.

- Account email: ${user?.email || ''}
- Account name: ${user?.name || ''}
- Plan: ${planLabel}
- Razorpay payment ID: [please paste from the Razorpay receipt you received over email]
- Date of payment: [please add]
- Reason (optional):

Thanks.`;
    return `mailto:support@creatorkhata.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleCancel = async () => {
    const periodEnd = formatDate(subscription?.currentPeriodEnd);
    const confirmMsg =
      `Cancel your PRO subscription?\n\n` +
      `• Your PRO access continues until ${periodEnd}\n` +
      `• After that, your account moves to the Free plan\n` +
      `• All your data — deals, invoices, ideas, rate cards — stays exactly as it is\n` +
      `• No further charges will be made\n\n` +
      `You can resubscribe anytime.\n\nProceed with cancellation?`;
    if (!window.confirm(confirmMsg)) return;

    setCancelling(true);
    setError('');
    setNotice('');
    try {
      await paymentAPI.cancelSubscription();
      await load();
      setNotice(`Subscription cancelled. You'll keep PRO access until ${periodEnd}.`);
    } catch (e) {
      setError(e.response?.data?.error
        || 'Cancellation failed. Please email support@creatorkhata.com for help.');
    } finally {
      setCancelling(false);
    }
  };

  const isFree         = !subscription || (user?.plan === 'FREE' && !subscription);
  const isCancelling   = subscription?.cancelAtPeriodEnd === true;
  const isActive       = subscription && !isCancelling && subscription.status === 'ACTIVE';
  const isPending      = subscription && subscription.status === 'CREATED';

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: '780px', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{
          fontFamily: "'Syne', sans-serif",
          fontSize: 'clamp(1.6rem, 3.5vw, 2.2rem)',
          fontWeight: 800, letterSpacing: '-0.02em',
          margin: '0 0 0.4rem', color: '#0f172a',
        }}>
          Account &amp; Subscription
        </h1>
        <p style={{ color: '#64748b', fontSize: '0.95rem', margin: 0 }}>
          Manage your plan, billing, and account details.
        </p>
      </div>

      {/* Profile card */}
      <section style={cardStyle}>
        <h2 style={sectionTitle}>Profile</h2>
        <div style={kvRow}><span style={kvKey}>Name</span><span style={kvVal}>{user?.name || '—'}</span></div>
        <div style={kvRow}><span style={kvKey}>Email</span><span style={kvVal}>{user?.email || '—'}</span></div>
        <div style={kvRow}><span style={kvKey}>Current plan</span>
          <span style={{
            ...kvVal,
            fontWeight: 700,
            // During the launch promo every account has PRO-equivalent access
            // (backend keeps raw plan=FREE; promo is dynamic). Show PRO here so
            // this badge doesn't contradict the "all features unlocked" banner
            // below — but label it as promo so it's honest about being temporary.
            color: (user?.plan === 'PRO' || isPromoActive()) ? '#8b5cf6' : '#64748b',
          }}>
            {user?.plan === 'PRO'
              ? 'PRO'
              : isPromoActive() ? 'PRO · Launch promo' : 'FREE'}
          </span>
        </div>
      </section>

      {/* Subscription card */}
      <section style={cardStyle}>
        <h2 style={sectionTitle}>Subscription</h2>

        {loading && <div style={{ color: '#64748b', fontSize: '0.9rem' }}>Loading…</div>}

        {!loading && isFree && isPromoActive() && (
          <div style={{
            background: 'linear-gradient(135deg, #fff7ed, #fef3c7)',
            border: '1px solid #fcd34d',
            borderRadius: 12,
            padding: '1rem 1.1rem',
            color: '#7c2d12',
            fontSize: '0.92rem',
            lineHeight: 1.55,
          }}>
            <strong>🎉 Launch promo active.</strong> All PRO features are unlocked
            for everyone until <strong>{PROMO_END_DISPLAY}</strong>. No card needed
            right now — enjoy Invoices, Contracts, Payment Tracker, Rate Card, Media
            Kit, Outreach, Collabs and Repurpose. We'll let you know before the
            promo ends.
          </div>
        )}

        {!loading && isFree && !isPromoActive() && (
          <div>
            <p style={{ margin: '0 0 1rem', color: '#475569', fontSize: '0.95rem' }}>
              You're on the <strong>Free</strong> plan — creator content workflow only:
              3 brand deals, 10 posts, 20 ideas/captions/trending each, 5 AI generations per day (shared).
              Upgrade to PRO to unlock Invoices, Contracts, Payment Tracker, Rate Card, Media Kit,
              Outreach, Collabs, and Repurpose.
            </p>
            <button onClick={() => setShowUpgrade(true)} style={primaryBtn}>
              Upgrade to PRO
            </button>
          </div>
        )}

        {!loading && isPending && (
          <div>
            <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '0.95rem' }}>
              Subscription created but payment not yet confirmed.
              Try the upgrade again, or contact support if you were charged.
            </p>
            <button onClick={() => setShowUpgrade(true)} style={primaryBtn}>
              Retry upgrade
            </button>
          </div>
        )}

        {!loading && isActive && (
          <div>
            <div style={kvRow}><span style={kvKey}>Plan</span>
              <span style={{ ...kvVal, fontWeight: 700, color: '#8b5cf6' }}>
                {cycleLabel(subscription.planKey)}
              </span>
            </div>
            <div style={kvRow}><span style={kvKey}>Status</span>
              <span style={{ ...kvVal, color: '#16a34a', fontWeight: 700 }}>Active</span>
            </div>
            <div style={kvRow}><span style={kvKey}>Next billing</span>
              <span style={kvVal}>{formatDate(subscription.currentPeriodEnd)}</span>
            </div>

            <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #f1f5f9' }}>
              <button onClick={handleCancel} disabled={cancelling} style={dangerBtn}>
                {cancelling ? 'Cancelling…' : 'Cancel subscription'}
              </button>
              <p style={{
                fontSize: '0.8rem', color: '#64748b',
                marginTop: '0.6rem', marginBottom: 0,
              }}>
                Access continues until the end of your current billing period.
                All your data stays intact.
              </p>
              <p style={{
                fontSize: '0.8rem', color: '#64748b',
                marginTop: '0.5rem', marginBottom: 0,
              }}>
                Within 7 days of payment? <a href={refundMailtoHref()} style={inlineLink}>
                  Request a refund instead
                </a> — money back, immediate downgrade. See <a href="/refund" style={inlineLink}>refund policy</a>.
              </p>
            </div>
          </div>
        )}

        {!loading && isCancelling && (
          <div>
            <div style={kvRow}><span style={kvKey}>Plan</span>
              <span style={{ ...kvVal, fontWeight: 700, color: '#8b5cf6' }}>
                {cycleLabel(subscription.planKey)}
              </span>
            </div>
            <div style={kvRow}><span style={kvKey}>Status</span>
              <span style={{ ...kvVal, color: '#d97706', fontWeight: 700 }}>
                Cancellation scheduled
              </span>
            </div>
            <div style={kvRow}><span style={kvKey}>Access until</span>
              <span style={kvVal}>{formatDate(subscription.currentPeriodEnd)}</span>
            </div>

            <div style={{
              marginTop: '1.25rem', padding: '0.85rem 1rem',
              background: '#fff7ed', border: '1px solid #fed7aa',
              borderRadius: '10px', fontSize: '0.85rem', color: '#9a3412',
            }}>
              Your subscription is cancelled but you keep PRO access until
              <strong> {formatDate(subscription.currentPeriodEnd)}</strong>. After that,
              your account moves to the Free plan and all your data stays intact.
              To resubscribe, <Link to="/contact"
              style={{ color: '#9a3412', fontWeight: 700 }}>contact us</Link>.
            </div>
            <p style={{
              fontSize: '0.8rem', color: '#64748b',
              marginTop: '0.85rem', marginBottom: 0,
            }}>
              Paid recently and want your money back instead? <a href={refundMailtoHref()} style={inlineLink}>
                Request a refund
              </a> if within 7 days of payment.
            </p>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: '1rem', padding: '0.75rem 0.9rem',
            background: '#fef2f2', border: '1px solid #fecaca',
            borderRadius: '10px', fontSize: '0.85rem', color: '#b91c1c',
          }}>{error}</div>
        )}
        {notice && (
          <div style={{
            marginTop: '1rem', padding: '0.75rem 0.9rem',
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: '10px', fontSize: '0.85rem', color: '#166534',
          }}>{notice}</div>
        )}
      </section>

      {/* Help card */}
      <section style={cardStyle}>
        <h2 style={sectionTitle}>Need help?</h2>
        <ul style={{ margin: 0, padding: '0 0 0 1.1rem', color: '#475569', fontSize: '0.9rem', lineHeight: 1.7 }}>
          <li>
            <strong>General support:</strong>{' '}
            <a href="mailto:support@creatorkhata.com" style={inlineLink}>
              support@creatorkhata.com
            </a>
          </li>
          <li>
            <strong>Refund request:</strong>{' '}
            <a href={refundMailtoHref()} style={inlineLink}>
              email with pre-filled details
            </a>{' '}
            (7-day window per payment — see{' '}
            <a href="/refund" style={inlineLink}>refund policy</a>)
          </li>
          <li>
            <strong>Legal:</strong>{' '}
            <a href="/terms" style={inlineLink}>terms</a> ·{' '}
            <a href="/privacy" style={inlineLink}>privacy</a> ·{' '}
            <a href="/refund" style={inlineLink}>refund</a>
          </li>
        </ul>
      </section>

      {/* Danger Zone — account deletion */}
      <section style={{ ...cardStyle, borderColor: '#fecaca', background: '#fefafa' }}>
        <h2 style={{ ...sectionTitle, color: '#991b1b' }}>Danger Zone</h2>
        <p style={{
          fontSize: '0.88rem', color: '#7f1d1d',
          marginTop: 0, marginBottom: '1rem', lineHeight: 1.55,
        }}>
          Permanently delete your CreatorKhata account, including all brand deals,
          invoices, payments, contracts, rate cards, and other data tied to your account.
          {user?.plan === 'PRO' && (
            <> Your active PRO subscription will be cancelled automatically.</>
          )}
        </p>
        <p style={{
          fontSize: '0.78rem', color: '#9a3412',
          margin: '0 0 1rem', lineHeight: 1.55,
          padding: '0.6rem 0.85rem', background: '#fef3c7',
          border: '1px solid #fcd34d', borderRadius: '8px',
        }}>
          ⚠ Recovery is possible for 30 days by emailing support — after that, deletion is permanent.
          Payment records are retained for 7 years per Indian tax law (disconnected from your identity).
        </p>
        <button
          onClick={() => setShowDelete(true)}
          style={{
            padding: '0.65rem 1.2rem',
            background: '#ef4444', color: 'white', border: 'none',
            borderRadius: '10px', fontSize: '0.88rem', fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Delete my account
        </button>
      </section>

      {showUpgrade && (
        <UpgradeModal
          onClose={() => setShowUpgrade(false)}
          onUpgraded={() => { setShowUpgrade(false); load(); }}
        />
      )}

      {showDelete && (
        <DeleteAccountModal
          userEmail={user?.email || ''}
          isPro={user?.plan === 'PRO'}
          subscriptionEndsOn={subscription?.currentPeriodEnd}
          onCancel={() => setShowDelete(false)}
          onConfirm={async (typedEmail, reason) => {
            await authAPI.deleteAccount({ confirmEmail: typedEmail, reason });
            // Clear all client state then redirect to landing with a one-time toast
            localStorage.clear();
            if (logout) {
              try { logout(); } catch (_) {}
            }
            navigate('/?account_deleted=1', { replace: true });
          }}
        />
      )}
    </div>
  );
}

// ── inline style helpers ─────────────────────────────────────────
const cardStyle = {
  background: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  padding: '1.5rem',
  marginBottom: '1.25rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
};

const sectionTitle = {
  fontFamily: "'Syne', sans-serif",
  fontSize: '1.1rem',
  fontWeight: 800,
  color: '#0f172a',
  letterSpacing: '-0.01em',
  margin: '0 0 1rem',
};

const kvRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.55rem 0',
  borderBottom: '1px solid #f8fafc',
};

const kvKey = { fontSize: '0.85rem', color: '#64748b', fontWeight: 600 };
const kvVal = { fontSize: '0.9rem',  color: '#0f172a', fontWeight: 500, textAlign: 'right' };

const primaryBtn = {
  padding: '0.7rem 1.25rem',
  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
  color: 'white',
  border: 'none',
  borderRadius: '10px',
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
};

const dangerBtn = {
  padding: '0.6rem 1.1rem',
  background: 'white',
  color: '#dc2626',
  border: '1.5px solid #fecaca',
  borderRadius: '10px',
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
};

const inlineLink = {
  color: '#8b5cf6',
  fontWeight: 600,
  textDecoration: 'underline',
};
